import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router';
import { ArrowLeft, Save01 } from '@untitledui/icons';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/base/buttons/button';
import { CompanyProfileStep1Fields } from '@/components/application/companies/company-profile-step1-fields';
import {
    ShipperOperationalProfileSection,
    type OperationalProfileVisibleSection,
} from '@/components/application/profile/shipper-operational-profile-section';
import { countries } from '@/data/countries';
import { getUserCompanies } from '@/data/api/companies';
import { useCompany, COMPANIES_QUERY_KEYS } from '@/hooks/api/use-companies';
import { useMe } from '@/hooks/api/use-auth';
import { apiPut } from '@/lib/api-client';
import { navigationStateFrom } from '@/types/navigation';
import { COMPANY_PROFILE_QUERY_KEY } from '@/hooks/api/use-company-profile';
import type { CompanyFormStep1Data, CompanyType } from '@/types/company';
import type { CompanyResponse, CompanyUpdate } from '@/types/api/admin';
import { CompanyType as ApiCompanyType } from '@/types/api/admin';
import { toast } from '@/lib/toast-service';
import { cx } from '@/utils/cx';

const emptyStep1 = (): CompanyFormStep1Data => ({
    company_type: '' as CompanyType | '',
    company_name: '',
    legal_name: '',
    entityType: '' as CompanyFormStep1Data['entityType'],
    tax_id: '',
    address: { street: '', city: '', state: '', postalCode: '' },
    country_code: '',
    primary_email: '',
    primary_phone: '',
    is_active: true,
    notes: '',
});

function companyResponseToStep1(company: CompanyResponse): CompanyFormStep1Data {
    const addr = company.headquarters_address as Record<string, string | undefined> | null;
    return {
        company_type: (company.company_type as string) as CompanyType,
        company_name: company.company_name,
        legal_name: company.legal_name ?? '',
        entityType: '',
        tax_id: company.tax_id ?? '',
        address: {
            street: addr?.street ?? '',
            city: addr?.city ?? '',
            state: addr?.state_province ?? '',
            postalCode: addr?.postal_code ?? '',
        },
        country_code: addr?.country_code ?? '',
        primary_email: company.primary_email ?? '',
        primary_phone: company.primary_phone ?? '',
        is_active: company.is_active,
        notes: company.notes ?? '',
    };
}

const validateStep1 = (data: CompanyFormStep1Data): boolean =>
    !!(
        data.company_type &&
        data.company_name.trim() &&
        data.legal_name.trim() &&
        data.tax_id.trim() &&
        data.address.street.trim() &&
        data.address.city.trim() &&
        data.address.state.trim() &&
        data.address.postalCode.trim() &&
        data.country_code &&
        data.primary_email.trim()
    );

const COMPANY_DETAILS_SECTIONS = ['info', 'hs', 'routes', 'carriers'] as const;
type CompanyDetailsSection = (typeof COMPANY_DETAILS_SECTIONS)[number];

const SECTION_LABEL_KEYS: Record<CompanyDetailsSection, string> = {
    info: 'companyEdit.nav.companyInfo',
    hs: 'companyEdit.nav.hsCodes',
    routes: 'companyEdit.nav.routes',
    carriers: 'companyEdit.nav.carriers',
};

function parseCompanyDetailsSection(raw: string | null): CompanyDetailsSection {
    if (raw === 'info' || raw === 'hs' || raw === 'routes' || raw === 'carriers') return raw;
    return 'info';
}

function operationalSectionFromNav(s: CompanyDetailsSection): OperationalProfileVisibleSection | null {
    if (s === 'info') return null;
    return s;
}

export function ProfileCompanyEditPage() {
    const { companyId } = useParams<{ companyId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation(['pages:profile', 'pages:companies', 'common']);
    const queryClient = useQueryClient();
    const { data: me } = useMe();

    const roleRoot = useMemo(() => {
        const seg = location.pathname.split('/').filter(Boolean)[0];
        return seg ? `/${seg}` : '/shipper';
    }, [location.pathname]);

    const backHref = `${roleRoot}/profile?tab=companies`;

    const { data: userCompanies = [], isLoading: loadingMembership } = useQuery({
        queryKey: ['user-companies'],
        queryFn: getUserCompanies,
        staleTime: 5 * 60 * 1000,
    });

    const membership = useMemo(
        () => userCompanies.find((c) => c.company_id === companyId),
        [userCompanies, companyId],
    );

    const canEdit =
        membership?.role === 'company_admin' ||
        membership?.role === 'super_admin' ||
        me?.role === 'super_admin';

    const { data: company, isLoading: loadingCompany, isError, error, refetch } = useCompany(companyId ?? '');

    const [step1, setStep1] = useState<CompanyFormStep1Data>(emptyStep1);
    const [stepValid, setStepValid] = useState(false);

    useEffect(() => {
        if (!companyId || !company) return;
        setStep1(companyResponseToStep1(company));
    }, [companyId, company]);

    useEffect(() => {
        setStepValid(validateStep1(step1));
    }, [step1]);

    useEffect(() => {
        if (!company) return;
        if (company.company_type !== ApiCompanyType.SENDER) {
            if (searchParams.has('section')) {
                const p = new URLSearchParams(searchParams);
                p.delete('section');
                setSearchParams(p, { replace: true });
            }
            return;
        }
        const raw = searchParams.get('section');
        if (!raw || !COMPANY_DETAILS_SECTIONS.includes(raw as CompanyDetailsSection)) {
            const p = new URLSearchParams(searchParams);
            p.set('section', 'info');
            setSearchParams(p, { replace: true });
        }
    }, [company, searchParams, setSearchParams]);

    const activeSection = useMemo(
        () => parseCompanyDetailsSection(searchParams.get('section')),
        [searchParams],
    );

    const handleSelectCompanySection = useCallback(
        (section: CompanyDetailsSection) => {
            const p = new URLSearchParams(searchParams);
            p.set('section', section);
            setSearchParams(p, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    useEffect(() => {
        if (loadingMembership || !companyId) return;
        if (me?.role === 'super_admin') return;
        if (userCompanies.length > 0 && !membership) {
            toast.error(t('companyEdit.notMember', { ns: 'pages:profile' }));
            navigate(backHref, { replace: true });
        }
    }, [loadingMembership, companyId, userCompanies.length, membership, me?.role, navigate, backHref, t]);

    const updateStep1 = useCallback((updates: Partial<CompanyFormStep1Data>) => {
        setStep1((prev) => ({ ...prev, ...updates }));
    }, []);

    const updateStep1Address = useCallback((updates: Partial<CompanyFormStep1Data['address']>) => {
        setStep1((prev) => ({
            ...prev,
            address: { ...prev.address, ...updates },
        }));
    }, []);

    const companyTypeOptions = useMemo(
        () => [
            { id: 'sender', label: t('form.fields.companyTypes.sender', { ns: 'pages:companies' }) },
            { id: 'receiver', label: t('form.fields.companyTypes.receiver', { ns: 'pages:companies' }) },
            { id: 'freight_forwarder', label: t('form.fields.companyTypes.freightForwarder', { ns: 'pages:companies' }) },
            { id: 'carrier', label: t('form.fields.companyTypes.carrier', { ns: 'pages:companies' }) },
            { id: 'customs_broker', label: t('form.fields.companyTypes.customsBroker', { ns: 'pages:companies' }) },
        ],
        [t],
    );

    const statusOptions = useMemo(
        () => [
            { id: 'true', label: t('list.status.active', { ns: 'pages:companies' }) },
            { id: 'false', label: t('list.status.inactive', { ns: 'pages:companies' }) },
        ],
        [t],
    );

    const countryOptions = useMemo(
        () => countries.map((c) => ({ id: c.id, label: c.label })),
        [],
    );

    const saveMutation = useMutation({
        mutationFn: async (payload: CompanyUpdate) => {
            return apiPut<CompanyResponse>(`/companies/${companyId}`, payload);
        },
        onSuccess: async () => {
            toast.success(t('companyEdit.saved', { ns: 'pages:profile' }));
            await queryClient.invalidateQueries({ queryKey: ['user-companies'] });
            await queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEYS.detail(companyId!) });
            await queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEYS.lists() });
            await queryClient.invalidateQueries({ queryKey: COMPANY_PROFILE_QUERY_KEY });
        },
        onError: (e: Error & { message?: string }) => {
            toast.error(e?.message || t('companyEdit.saveError', { ns: 'pages:profile' }));
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!canEdit || !companyId || !stepValid) return;

        const payload: CompanyUpdate = {
            company_name: step1.company_name,
            company_type: step1.company_type as ApiCompanyType,
            legal_name: step1.legal_name,
            tax_id: step1.tax_id,
            address_line1: step1.address.street,
            city: step1.address.city,
            state_province: step1.address.state,
            postal_code: step1.address.postalCode,
            country_code: step1.country_code,
            primary_email: step1.primary_email,
            primary_phone: step1.primary_phone || undefined,
            is_active: step1.is_active,
            notes: step1.notes || undefined,
        };
        saveMutation.mutate(payload);
    };

    if (!companyId) {
        return null;
    }

    const loading = loadingMembership || loadingCompany;

    if (loading && !company) {
        return (
            <div className="flex min-h-[12rem] items-center justify-center text-tertiary">
                {t('common:loading')}
            </div>
        );
    }

    if (isError) {
        return (
            <div className="space-y-4 rounded-xl border border-secondary bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <p className="text-primary">{t('companyEdit.loadError', { ns: 'pages:profile' })}</p>
                <p className="text-sm text-tertiary">{(error as Error)?.message}</p>
                <div className="flex flex-wrap gap-2">
                    <Button color="secondary" size="sm" onClick={() => refetch()}>
                        {t('companyEdit.retry', { ns: 'pages:profile' })}
                    </Button>
                    <Button
                        type="button"
                        color="secondary"
                        size="sm"
                        onClick={() => navigate(backHref, { state: navigationStateFrom(location) })}
                    >
                        {t('companyEdit.back', { ns: 'pages:profile' })}
                    </Button>
                </div>
            </div>
        );
    }

    const isSender = company?.company_type === ApiCompanyType.SENDER;

    const companyInfoForm = (
        <form onSubmit={handleSubmit} className="space-y-6">
            <CompanyProfileStep1Fields
                step1={step1}
                updateStep1={updateStep1}
                updateStep1Address={updateStep1Address}
                companyTypeOptions={companyTypeOptions}
                countryOptions={countryOptions}
                statusOptions={statusOptions}
                readOnly={!canEdit}
                showStatusField={false}
            />

            {canEdit && (
                <div className="flex justify-end">
                    <Button
                        type="submit"
                        color="primary"
                        iconLeading={Save01}
                        isLoading={saveMutation.isPending}
                        isDisabled={!stepValid}
                    >
                        {t('companyEdit.save', { ns: 'pages:profile' })}
                    </Button>
                </div>
            )}
        </form>
    );

    return (
        <div className="space-y-6">
            <div className="space-y-4">
                <Button
                    type="button"
                    size="sm"
                    color="secondary"
                    iconLeading={ArrowLeft}
                    onClick={() => navigate(backHref, { state: navigationStateFrom(location) })}
                >
                    {t('companyEdit.back', { ns: 'pages:profile' })}
                </Button>
                <div>
                    <h1 className="text-2xl font-semibold text-primary">
                        {t('companyEdit.title', { ns: 'pages:profile' })}
                    </h1>
                    <p className="mt-1 text-tertiary">
                        {company?.company_name ?? membership?.company_name ?? '—'}
                    </p>
                </div>
            </div>

            {!canEdit && (
                <div
                    className="rounded-lg border border-secondary bg-secondary/30 px-4 py-3 text-sm text-secondary"
                    role="status"
                >
                    {t('companyEdit.readOnlyHint', { ns: 'pages:profile' })}
                </div>
            )}

            {isSender && companyId ? (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr] lg:items-start">
                    <nav
                        className="flex flex-row gap-1 overflow-x-auto pb-1 lg:flex-col lg:overflow-x-visible lg:pb-0 lg:pr-2"
                        aria-label={t('companyEdit.nav.ariaLabel', { ns: 'pages:profile' })}
                    >
                        {COMPANY_DETAILS_SECTIONS.map((section) => {
                            const isActive = activeSection === section;
                            return (
                                <button
                                    key={section}
                                    type="button"
                                    onClick={() => handleSelectCompanySection(section)}
                                    className={cx(
                                        'flex shrink-0 items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-semibold outline-focus-ring transition-colors',
                                        'min-h-11 lg:min-h-0 lg:border-l-2',
                                        isActive
                                            ? 'bg-primary_hover text-brand-secondary lg:border-fg-brand-primary_alt'
                                            : 'text-tertiary hover:bg-primary_hover hover:text-primary lg:border-transparent',
                                    )}
                                    aria-current={isActive ? 'page' : undefined}
                                >
                                    <span className="truncate">
                                        {t(SECTION_LABEL_KEYS[section], { ns: 'pages:profile' })}
                                    </span>
                                </button>
                            );
                        })}
                    </nav>

                    <div className="min-w-0">
                        {activeSection === 'info' ? (
                            companyInfoForm
                        ) : (
                            <ShipperOperationalProfileSection
                                companyId={companyId}
                                readOnly={!canEdit}
                                visibleSection={operationalSectionFromNav(activeSection)!}
                            />
                        )}
                    </div>
                </div>
            ) : (
                companyInfoForm
            )}
        </div>
    );
}
